// src/support/attainmentLeader.ts
//
// Pure helpers for the Leader > Attainment tab. Unlike every other Leader tab, its reporting structure
// is NOT the User Access org chart: it is discovered level by level from the CENTRO
// `goal/attainment/drill/by-email/` responses (each node lists its direct reports). These helpers turn
// the chain of fetched drill nodes into synthetic `OrgChartRow`s so the shared Leader filter bar (and
// `getDirectReports` / `isManagerEmail`) can drive the dropdowns unchanged.

import type {
  AttainmentDrillNode,
  AttainmentStats,
  AttainmentPeriodElement,
  AttainmentPeriod,
  AttainmentPeriodMeta,
} from '../hooks/attainment-hooks';
import type { OrgChartRow } from './orgScope';
import type { ChartPoint } from './campaignLeaderCharts';
import { attainmentPct, round2 } from './enablementLeader';

const norm = (e: string | null | undefined) => String(e || '').trim().toLowerCase();

/**
 * Synthetic org rows from drill nodes (root first; `null` = level not loaded yet). Every loaded node
 * contributes its own row plus a row per direct report parented to it. A report that is later loaded as
 * a node keeps one row (ids are assigned per email), with `is_manager` taken from the loaded node.
 */
export function buildAttainmentOrgRows(nodes: Array<AttainmentDrillNode | null>): OrgChartRow[] {
  const ids = new Map<string, number>();
  const rows = new Map<string, OrgChartRow>();
  const idFor = (email: string) => {
    if (!ids.has(email)) ids.set(email, ids.size + 1);
    return ids.get(email)!;
  };

  for (const node of nodes) {
    if (!node?.email) continue;
    const email = norm(node.email);
    const existing = rows.get(email);
    rows.set(email, {
      ...(existing ?? { user_id: idFor(email), user_email: email }),
      is_manager: !!node.is_manager && (node.reports?.length ?? 0) > 0,
    });

    for (const r of node.reports ?? []) {
      const reportEmail = norm(r.email);
      if (!reportEmail || reportEmail === email) continue;
      const prior = rows.get(reportEmail);
      rows.set(reportEmail, {
        user_id: idFor(reportEmail),
        user_email: reportEmail,
        // Keep a loaded node's own verdict; otherwise trust the stub.
        is_manager: prior ? prior.is_manager : !!r.is_manager,
        manager_id: idFor(email),
      });
    }
  }

  return Array.from(rows.values());
}

const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The current goaling period: the one whose `start`–`end` window contains `today`. Between intervals
 * (or if dates are missing) falls back to the most recent period that has started, then to the first
 * (periods arrive newest-first). Null when there are none. Works on full periods or bare metas.
 */
export function currentAttainmentPeriod<T extends AttainmentPeriodMeta>(
  periods: T[] | null | undefined,
  today: Date = new Date(),
): T | null {
  if (!periods?.length) return null;
  const day = isoDay(today);
  const within = periods.find((p) => p.start && p.end && p.start <= day && day <= p.end);
  if (within) return within;
  const started = periods
    .filter((p) => p.start && p.start <= day)
    .sort((a, b) => String(b.start).localeCompare(String(a.start)));
  return started[0] ?? periods[0];
}

/** `Jul 26, 2026 – Jan 23, 2027` for a period's window ('' when either end is missing). */
export function formatPeriodRange(p: Pick<AttainmentPeriod, 'start' | 'end'> | null | undefined): string {
  if (!p?.start || !p?.end) return '';
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  return `${fmt(p.start)} – ${fmt(p.end)}`;
}

const sameLabel = (a?: string | null, b?: string | null) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/** The period with this `label` (case-insensitive), or null. */
export function findPeriodByLabel<T extends AttainmentPeriodMeta>(
  periods: T[] | null | undefined,
  label: string | null | undefined,
): T | null {
  if (!label) return null;
  return periods?.find((p) => sameLabel(p.label, label)) ?? null;
}

/**
 * Selectable intervals, newest first. Prefers the server's `available_periods` (the whole subtree's
 * intervals) and otherwise unions the per-person periods carried on the loaded nodes — so the picker
 * still works against a server that doesn't send the list.
 */
export function intervalOptions(
  available: AttainmentPeriodMeta[],
  nodes: Array<AttainmentDrillNode | null>,
): AttainmentPeriodMeta[] {
  if (available.length) return available;
  const byLabel = new Map<string, AttainmentPeriodMeta>();
  for (const n of nodes) {
    const people = [n?.own, ...(n?.reports ?? []).map((r) => r.own)];
    for (const own of people) {
      for (const p of own?.periods ?? []) {
        const key = String(p.label ?? p.sheet_id);
        if (!byLabel.has(key)) {
          byLabel.set(key, {
            sheet_id: p.sheet_id,
            interval: p.interval,
            label: p.label,
            start: p.start,
            end: p.end,
          });
        }
      }
    }
  }
  return Array.from(byLabel.values()).sort((a, b) => String(b.end ?? '').localeCompare(String(a.end ?? '')));
}

// ---- plan elements (SEC-GROWTH-ACV|PRI, SEC-TTLSEC-S-XA|NPR …) ------------

/** Axis/legend name for a plan element: its category, else its type, never blank. */
export function elementLabel(el: Pick<AttainmentPeriodElement, 'category' | 'type'>): string {
  return (el.category || el.type || 'Element').toString();
}

/** Dollars in millions to two decimals — the unit plan-element charts plot in. */
export function usdToMillions(n: number | null | undefined): number {
  return round2((Number(n) || 0) / 1e6);
}

export type ElementMeasure = 'quota' | 'bookings' | 'attainment';

/** One bar per plan element for a measure: quota / bookings in USD millions, attainment as a percent. */
export function elementRows(
  elements: AttainmentPeriodElement[] | null | undefined,
  measure: ElementMeasure,
): ChartPoint[] {
  return (elements ?? []).map((el) => ({
    label: elementLabel(el),
    value:
      measure === 'attainment'
        ? (attainmentPct(el) ?? 0)
        : usdToMillions(measure === 'quota' ? el.quota : el.bookings),
  }));
}

/** Each element's share of total bookings (donut) — elements with no bookings are dropped. */
export function elementBookingsMixRows(
  elements: AttainmentPeriodElement[] | null | undefined,
): ChartPoint[] {
  return (elements ?? [])
    .filter((el) => (el.bookings ?? 0) > 0)
    .map((el) => ({ label: elementLabel(el), value: usdToMillions(el.bookings) }));
}

/** Quota / bookings / attainment summed across the given elements (the table's total row). */
export function elementsTotal(elements: AttainmentPeriodElement[] | null | undefined) {
  const quota = (elements ?? []).reduce((a, e) => a + (e.quota ?? 0), 0);
  const bookings = (elements ?? []).reduce((a, e) => a + (e.bookings ?? 0), 0);
  return { quota, bookings, attainment_pct: quota ? bookings / quota : null };
}

// ---- interval data ---------------------------------------------------------
//
// Every figure on the Attainment tab is read from a person's own per-period detail
// (`node.own.periods[]`, matched by the selected interval's label). The drill API's `rollup` objects are
// NEVER used: they sum a manager's whole subtree, which double-counts because a manager's own plan
// quota already covers their team. A person with no plan in the selected interval has no period with
// that label and so shows no data, never another interval's figures.

export type IntervalStats = Pick<AttainmentStats, 'quota' | 'bookings' | 'attainment_pct'>;

/** A person's totals for exactly `label`, read from their per-period detail — null when they have no plan then. */
export function statsForInterval(
  own: Pick<AttainmentStats, 'periods'> | null | undefined,
  label: string | null | undefined,
): IntervalStats | null {
  return findPeriodByLabel(own?.periods, label)?.total ?? null;
}

/** True when the stats carry any quota or bookings (an all-zero / null total is "no data"). */
export function hasIntervalData(stats: IntervalStats | null | undefined): boolean {
  return !!stats && ((stats.quota ?? 0) !== 0 || (stats.bookings ?? 0) !== 0);
}

/** Whether a team page has figures for `label`: `ok`, or `none` (render only a "no data" note). */
export function teamDataState(node: AttainmentDrillNode, label: string | null | undefined): 'ok' | 'none' {
  return hasIntervalData(statsForInterval(node.own, label)) ? 'ok' : 'none';
}

export interface UsdScale {
  divisor: number;
  unit: 'billions' | 'millions' | 'thousands';
}

/**
 * The USD unit a chart should plot in, from the biggest value on it: billions for a big org, millions for
 * a sub-team, thousands below that. A fixed billions axis flattened every sub-team bookings bar to 0.00.
 */
export function usdScaleFor(values: Array<number | null | undefined>): UsdScale {
  const peak = values.reduce<number>((m, v) => Math.max(m, Math.abs(Number(v) || 0)), 0);
  if (peak >= 1e9) return { divisor: 1e9, unit: 'billions' };
  if (peak >= 1e6) return { divisor: 1e6, unit: 'millions' };
  return { divisor: 1e3, unit: 'thousands' };
}

export const scaleUsd = (n: number | null | undefined, scale: UsdScale) =>
  round2((Number(n) || 0) / scale.divisor);

/** Per direct report with data in `label`: that person's own figures for the interval (managers included). */
function reportStatsForInterval(node: AttainmentDrillNode, label: string | null | undefined) {
  const out: Array<{ label: string; stats: IntervalStats }> = [];
  for (const r of node.reports ?? []) {
    const stats = statsForInterval(r.own, label);
    if (!stats || !hasIntervalData(stats)) continue;
    const at = r.email.indexOf('@');
    out.push({ label: at > 0 ? r.email.slice(0, at) : r.email, stats });
  }
  return out;
}

/** Attainment % per direct report with data in the interval (reports without data are left out, not 0). */
export function reportAttainmentRows(
  node: AttainmentDrillNode,
  label: string | null | undefined,
): ChartPoint[] {
  return reportStatsForInterval(node, label).map(({ label: l, stats }) => ({
    label: l,
    value: attainmentPct(stats) ?? 0,
  }));
}

/** Bookings per direct report with data in the interval, in an auto-chosen unit (see {@link usdScaleFor}). */
export function reportBookingsRows(
  node: AttainmentDrillNode,
  label: string | null | undefined,
): { rows: ChartPoint[]; scale: UsdScale } {
  const items = reportStatsForInterval(node, label);
  const scale = usdScaleFor(items.map((i) => i.stats.bookings));
  return {
    rows: items.map(({ label: l, stats }) => ({ label: l, value: scaleUsd(stats.bookings, scale) })),
    scale,
  };
}

/** Quota vs bookings as two bars in an auto-chosen unit. */
export function quotaBookingsRows(
  stats: Pick<AttainmentStats, 'quota' | 'bookings'> | null | undefined,
): { rows: ChartPoint[]; scale: UsdScale } {
  const scale = usdScaleFor([stats?.quota, stats?.bookings]);
  return {
    rows: [
      { label: 'Quota', value: scaleUsd(stats?.quota, scale) },
      { label: 'Bookings', value: scaleUsd(stats?.bookings, scale) },
    ],
    scale,
  };
}
